import * as styles from "./Button.css";

import { clsx } from "clsx";
import { type JSX, isValidElement, useMemo, useRef } from "react";

import { Button as BaseButton } from "@base-ui/react/button";
import { KbdShortcut } from "@frontend/components/atoms/Kbd";
import { Ripple } from "@frontend/components/atoms/Ripple";
import {
  toAriaKeyShortcuts,
  useScopedShortcut,
} from "@frontend/hooks/useScopedShortcut";
import { mergeRefs } from "@frontend/utils/mergeRefs";

import { ButtonContext, makeButtonContext } from "./Button.context";
import type { ButtonProps } from "./Button.types";

export function Button({
  size,
  variant,
  children,
  className,
  loading,
  disableRipple,
  leftSection,
  rightSection,
  nativeButton,
  shortcut,
  showShortcut = true,
  ref,
  ...props
}: ButtonProps): JSX.Element {
  const label = props.label ?? children;

  const buttonRef = useRef<HTMLElement>(null);

  const mergedRef = useMemo(() => mergeRefs(buttonRef, ref), [ref]);

  useScopedShortcut(shortcut, () => buttonRef.current?.click(), {
    enabled: !props.disabled && !loading,
    available: () => isActionable(buttonRef.current),
  });

  const isNativeButton =
    nativeButton ??
    (props.render === undefined ||
      (isValidElement(props.render) && props.render.type === "button"));

  const keys = shortcut && showShortcut && (
    <KbdShortcut className={styles.shortcut} shortcut={shortcut} />
  );

  const buttonContent =
    leftSection || rightSection || keys ? (
      <>
        {leftSection}
        {label !== undefined && label !== "" && <span>{label}</span>}
        {rightSection}
        {keys}
      </>
    ) : (
      label
    );

  return (
    <ButtonContext.Provider value={makeButtonContext({ variant })}>
      <BaseButton
        data-slot="button"
        className={clsx(styles.button({ size, variant }), className)}
        nativeButton={isNativeButton}
        aria-keyshortcuts={shortcut && toAriaKeyShortcuts(shortcut)}
        ref={mergedRef}
        {...props}
      >
        {loading ? "Loading..." : buttonContent}
        {!disableRipple && <Ripple />}
      </BaseButton>
    </ButtonContext.Provider>
  );
}

function isActionable(element: HTMLElement | null): boolean {
  return (
    element !== null &&
    element.isConnected &&
    element.checkVisibility() &&
    !element.matches(":disabled, [aria-disabled='true'], [data-disabled]")
  );
}
